import type { BindingConfig } from '@wix/wix-data-client-common'
import type {
  DigestedSchema,
  DigestedSchemaField,
} from '@wix/wix-data-client-common-standalone'

type FieldForPathLookup = {
  name?: string
  type: DigestedSchemaField['type']
  condition?: DigestedSchemaField['condition']
  fields?: Record<string, FieldForPathLookup>
  elementType?: FieldForPathLookup
}

const hasFieldPath = (
  binding: BindingConfig,
): binding is Extract<BindingConfig, { fieldPath: string }> =>
  binding.type === 'Field' || binding.type === 'Filter'

const getFieldFromFieldCollection = (
  fields: FieldForPathLookup[] | Record<string, FieldForPathLookup> | undefined,
  fieldName: string,
): FieldForPathLookup | undefined => {
  if (!fields) {
    return undefined
  }

  return Array.isArray(fields)
    ? fields.find(field => field.name === fieldName)
    : fields[fieldName]
}

export const getFieldByPathSegments = (
  field: FieldForPathLookup | undefined,
  pathSegments: string[] = [],
): FieldForPathLookup | undefined => {
  if (!field) {
    return undefined
  }

  if (pathSegments.length === 0) {
    return field
  }

  if (field.type === 'object' && field.fields) {
    const [nextSegment, ...remainingSegments] = pathSegments
    return getFieldByPathSegments(field.fields[nextSegment], remainingSegments)
  }

  if (field.type === 'array' && field.elementType) {
    return getFieldByPathSegments(field.elementType, pathSegments)
  }

  return undefined
}

export const getFieldByPath = (
  collection: Pick<DigestedSchema, 'fields'>,
  fieldPath: string,
): FieldForPathLookup | undefined => {
  const exactField = getFieldFromFieldCollection(collection.fields, fieldPath)
  if (exactField) {
    return exactField
  }

  const [rootSegment, ...remainingSegments] = fieldPath.split('.')
  const rootField = getFieldFromFieldCollection(collection.fields, rootSegment)

  return getFieldByPathSegments(rootField, remainingSegments)
}

export const getFieldGroupNames = (
  bindings: BindingConfig[],
  collections: DigestedSchema[],
  collectionName: string | undefined,
): string[] => {
  const collection = collections.find(({ id }) => id === collectionName)
  if (!collection) {
    return []
  }

  return [
    ...new Set(
      bindings
        .filter(hasFieldPath)
        .map(({ fieldPath }) => getFieldByPath(collection, fieldPath))
        .map(field => field?.condition?.groupName)
        .filter((groupName): groupName is string => !!groupName),
    ),
  ]
}

export const hasFieldGroups = (schema?: DigestedSchema): boolean =>
  !!schema?.fields?.some(field => !!field.condition?.groupName)
